import React from 'react';

import {
    FFMap,
    AeroLayer,
    FFBasemapLayer,
    FFChartLayer,
    FFRasterMapLayer,
    FFTrackLayer,
    PlaceLabelsLayer,
} from '@foreflight/webmap/react';

import {
    useDebriefSegments,
    useCurrentTracklog,
    useMapControls,
    useMapSettings,
} from 'ff/debrief/hooks';
import MapContent from 'ff/debrief/layouts/MapContent';

import { isFeatureEnabled } from 'ff/common/ffAppUtils';
import TrackLayer from 'ff/debrief/components/map/TrackLayer';
import ApproachPlanLayer from 'ff/debrief/components/map/ApproachPlanLayer';
import { AirportSettings } from 'ff/debrief/models';
import { LngLat } from '@foreflight/webmap/core';

interface Props {}

const Map = (props: React.PropsWithChildren<Props>) => {
    const { coordinates, viewport } = useCurrentTracklog();
    const { currentSettings, boundsPadding } = useMapSettings();
    const { segmentTree, selectedSegment } = useDebriefSegments();

    // @ts-expect-error TODO: this shouldn't be a problem because the 2D engine
    // should just ignore the 3rd altitude value, but we still need to clean up
    // the types
    const lngLats = coordinates as LngLat[];

    return (
        <MapContent>
            {/* Only known way to set the height correctly. 50px accounts for header. */}
            {/* @ts-expect-error Issues with webmap exports */}
            <FFMap
                height="calc(100vh - 50px)"
                theme={currentSettings?.theme}
                viewport={viewport}
                boundsPadding={boundsPadding}
                resourcesUrl="{apiRoot}/map/api/resources"
            >
                <MapLayers />
                {isFeatureEnabled('debrief_features') &&
                lngLats &&
                segmentTree ? (
                    <TrackLayer
                        trackCoords={lngLats}
                        segments={segmentTree.getScoredSegments()}
                        selectedSegment={selectedSegment}
                    />
                ) : (
                    <FFTrackLayer points={lngLats} />
                )}
                {props.children}
                {isFeatureEnabled('debrief_iap') &&
                    selectedSegment?.isApproachSegment() && (
                        <ApproachPlanLayer />
                    )}
            </FFMap>
        </MapContent>
    );
};

const MapLayers = () => {
    const { currentLayer, aeroLayer } = useMapControls();
    const { currentSettings, allAeroLayersDisabled } = useMapSettings();
    const culturalElements = currentSettings?.culturalElements;
    let airportSettings: AirportSettings | boolean = false;
    if (
        currentSettings?.airports &&
        Boolean(currentSettings?.filters?.airports)
    ) {
        if (currentSettings?.filters?.airports) {
            airportSettings = currentSettings?.filters?.airports;
        } else {
            airportSettings = false;
        }
    }
    const aeronavFilters = { ...currentSettings?.filters };

    return (
        <>
            <FFBasemapLayer
                major_roads={culturalElements?.major_roads}
                minor_roads={culturalElements?.minor_roads}
                railroads={culturalElements?.railroads}
                urban_areas={culturalElements?.urban_areas}
                forests_parks={culturalElements?.forests_parks}
                peaks_passes={culturalElements?.peaks_passes}
                points_of_interest={culturalElements?.points_of_interest}
                power_lines={culturalElements?.power_lines}
            />
            {currentSettings?.placeLabels && <PlaceLabelsLayer />}
            {/* Hacky method to remove Aero Layer from Dom when last setting changes to false*/}
            {!allAeroLayersDisabled() && aeroLayer && (
                <AeroLayer
                    filters={{
                        airspace: currentSettings.airspace && {
                            ...currentSettings?.airspaceFilters,
                        },
                        artcc: false,
                        artccfir: false,
                        artccfisa: false,
                        atcsec: false,
                        styleOptions: { airwayLabelMinzoom: true },
                        airports: currentSettings?.airports && airportSettings,
                        navlevel: aeronavFilters?.navlevel,
                        airways: aeronavFilters?.airways,
                        navaids: aeronavFilters?.navaids,
                        waypoints: aeronavFilters?.waypoints,
                        euData: false,
                    }}
                />
            )}

            {(currentLayer === 'street' || currentLayer === 'aerial') && (
                <FFRasterMapLayer name={currentLayer} />
            )}
            {currentLayer &&
                currentLayer !== 'street' &&
                currentLayer !== 'aerial' && (
                    <FFChartLayer name={currentLayer} />
                )}
        </>
    );
};

export default Map;
